import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { RowData } from '@tanstack/react-table';
import type { LegacyRow } from '@tanstack/react-table/legacy';
import { Table, TableCaption } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { DataTableBody } from './data-table-body';
import { DataTableBulkBar } from './data-table-bulk-bar';
import { DataTableHead } from './data-table-head';
import { DataTableSkeleton } from './data-table-skeleton';
import { DataTableStates } from './data-table-states';
import { DataTableToolbar } from './data-table-toolbar';
import { useDataTable } from './use-data-table';
import { useScrollEdges } from './use-scroll-edges';
import { useTableKeyboard } from './use-table-keyboard';
import { useVirtualRows } from './use-virtual-rows';
import { HEADER_HEIGHT, ROW_HEIGHT, VIRTUALIZE_THRESHOLD, type DataTableProps } from './types';

/** Rows-as-links, keyboard navigation, windowing and the five states, in one table. */
export function DataTable<TData extends RowData>(props: DataTableProps<TData>) {
  const {
    data,
    loading = false,
    error,
    onRetry,
    errorTitle,
    isFiltered = false,
    onClearFilters,
    emptyTitle = 'Todavía no hay registros',
    emptyDescription = 'Cuando se cree el primero, aparecerá acá.',
    emptyAction,
    rowHref,
    onRowOpen,
    selectable = false,
    onSelectionChange,
    bulkActions,
    rowActions,
    rowLabel,
    rowDetail,
    expandedIds,
    pinFirstColumn = false,
    numericColumns,
    fillColumns,
    toolbar,
    hideToolbar = false,
    hideRowCount = false,
    defaultSortLabel,
    hideDensity = false,
    virtualizeThreshold = VIRTUALIZE_THRESHOLD,
    caption,
    className,
  } = props;

  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  const { table, setRowSelection, density, setDensity, columnEntries, ...columnApi } = useDataTable({
    ...props,
    selectable,
  });

  const rows = table.getRowModel().rows;
  const rowHeight = ROW_HEIGHT[density];
  const pinning = { pinFirstColumn, selectable };
  const leafColumns = table.getVisibleLeafColumns();
  const visibleColumns = leafColumns.length;
  const totalColumns = visibleColumns + (selectable ? 1 : 0) + (rowActions ? 1 : 0);
  const openable = Boolean(rowHref || onRowOpen);

  const edges = useScrollEdges(
    containerRef,
    leafColumns.map((column) => column.id).join(','),
  );

  // El ventaneo mide las filas por aritmética: todas del mismo alto, sin medir
  // nada (ver `use-virtual-rows`). Un detalle abierto rompe justo esa premisa,
  // así que mientras haya uno abierto se apaga y se dibujan todas. El alto total
  // no cambia al hacerlo —los rellenos de arriba y abajo valían lo mismo que las
  // filas que ocultaban—, de modo que la posición del scroll se mantiene.
  const detalleAbierto = Boolean(rowDetail) && (expandedIds?.size ?? 0) > 0;

  const virtual = useVirtualRows({
    count: rows.length,
    rowHeight,
    containerRef,
    enabled: rows.length > virtualizeThreshold && !loading && !detalleAbierto,
  });

  const openRow = (row: LegacyRow<TData>, options: { newTab: boolean }) => {
    if (onRowOpen) {
      onRowOpen(row.original, options);
      return;
    }
    const href = rowHref?.(row.original);
    if (!href) return;
    if (options.newTab) window.open(href, '_blank', 'noopener');
    else navigate(href);
  };

  const { focusedIndex, setFocusedIndex } = useTableKeyboard({
    containerRef,
    rowCount: rows.length,
    rowHeight,
    headerHeight: HEADER_HEIGHT,
    onOpen: (index) => {
      const row = rows[index];
      if (row && openable) openRow(row, { newTab: false });
    },
    onToggleSelect: (index) => rows[index]?.toggleSelected(),
    onEscape: () => setRowSelection({}),
    enabled: !loading,
  });

  const selectedRows = rows.filter((row) => row.getIsSelected());
  const selectedIds = selectedRows.map((row) => row.id);
  const selectionKey = selectedIds.join(',');
  const selectedRowsRef = useRef<TData[]>([]);
  selectedRowsRef.current = selectedRows.map((row) => row.original);

  // Keyed on the selection alone: `rows` is a new array on every render, and
  // re-notifying on each of them would loop through the caller's state.
  useEffect(() => {
    const ids = selectionKey ? selectionKey.split(',') : [];
    onSelectionChange?.(ids, selectedRowsRef.current);
  }, [selectionKey, onSelectionChange]);

  const state = loading
    ? 'loading'
    : error
      ? 'error'
      : rows.length === 0
        ? isFiltered
          ? 'no-results'
          : 'empty'
        : 'ready';

  return (
    <div className={cn('flex min-h-0 flex-1 flex-col', className)}>
      {!hideToolbar && (
        <DataTableToolbar
          rowCount={rows.length}
          totalCount={data.length}
          hideRowCount={hideRowCount}
          density={density}
          defaultSortLabel={defaultSortLabel}
          hideDensity={hideDensity}
          onDensityChange={setDensity}
          columns={columnEntries}
          onToggleColumn={columnApi.toggleColumn}
          onMoveColumn={columnApi.moveColumn}
          onResetColumns={columnApi.resetColumns}
          {...(props.sort ? { sort: props.sort } : {})}
          {...(props.onSortChange ? { onSortChange: props.onSortChange } : {})}
          {...(props.columnLabels ? { columnLabels: props.columnLabels } : {})}
        >
          {toolbar}
        </DataTableToolbar>
      )}

      <div className="relative min-h-0 flex-1">
        <div
          ref={containerRef}
          data-hidden-left={edges.left}
          data-hidden-right={edges.right}
          className="group/scroll h-full overflow-auto scrollbar-thin">
          <Table {...(openable || selectable ? { role: 'grid' } : {})}>
            {caption && <TableCaption className="sr-only">{caption}</TableCaption>}
            <DataTableHead
              table={table}
              pinning={pinning}
              hasRowActions={Boolean(rowActions)}
              {...(numericColumns ? { numericColumns } : {})}
            />

            {state === 'loading' && (
              <DataTableSkeleton
                rows={8}
                columns={visibleColumns}
                rowHeight={rowHeight}
                selectable={selectable}
              />
            )}

            {state === 'ready' && (
              <DataTableBody
                rows={rows}
                window={virtual}
                rowHeight={rowHeight}
                totalColumns={totalColumns}
                pinning={pinning}
                focusedIndex={focusedIndex}
                onFocusRow={setFocusedIndex}
                onOpenRow={openRow}
                openable={openable}
                {...(rowActions ? { rowActions } : {})}
                {...(rowLabel ? { rowLabel } : {})}
                {...(rowDetail ? { rowDetail } : {})}
                {...(expandedIds ? { expandedIds } : {})}
                {...(fillColumns ? { fillColumns } : {})}
              />
            )}

            {state !== 'loading' && state !== 'ready' && (
              <DataTableStates
                state={state}
                columns={totalColumns}
                emptyTitle={emptyTitle}
                emptyDescription={emptyDescription}
                {...(errorTitle ? { errorTitle } : {})}
                {...(error instanceof Error && error.message
                  ? { errorDescription: error.message }
                  : {})}
                {...(onRetry ? { onRetry } : {})}
                {...(onClearFilters ? { onClearFilters } : {})}
                {...(emptyAction ? { emptyAction } : {})}
              />
            )}
          </Table>
        </div>

        {/* The cut, made visible. The fade sits below the sticky cells (z-10 vs
            z-20) so it darkens only what is scrolling away, and clears the
            actions rail so the rail keeps reading as a rail and not as fog. */}
        <div
          aria-hidden
          // Opacity inline, not as a utility: `aria-hidden` elements are driven
          // to `opacity: 0` by the animation layer, and a class cannot outrank
          // that without an `!important` that would then never fade back out.
          style={{ opacity: edges.right ? 1 : 0 }}
          className={cn(
            'pointer-events-none absolute inset-y-0 w-10 bg-gradient-to-l from-background to-transparent transition-opacity duration-150 ease-out',
            rowActions ? 'right-12' : 'right-0',
          )}
        />

        {selectable && bulkActions && (
          <DataTableBulkBar count={selectedIds.length} onClear={() => setRowSelection({})}>
            {bulkActions({
              ids: selectedIds,
              rows: selectedRows.map((row) => row.original),
              clear: () => setRowSelection({}),
            })}
          </DataTableBulkBar>
        )}
      </div>
    </div>
  );
}
